// Scene renderer (react-konva) over the shared sceneToKonva mapping. Two modes:
//  - editable=false (default): pure presentational render, identical to the export
//    path (the lambda's renderSceneNode.js) -> WYSIWYG parity. NO selection/
//    transform chrome.
//  - editable=true: editor mode — editable elements are click-to-select, drag to
//    move, and resize/rotate via a Konva.Transformer. Geometry edits are reported
//    via onTransformChange and re-applied (by sceneToKonva) on both editor + export.

import React from 'react';
import { Image as KonvaImage, Layer, Line, Rect, Shape, Stage, Text, Transformer } from 'react-konva';

import { ensureSceneFontsLoaded, registerSceneFonts } from './fonts';
import { computeSnap } from '../components/admin/canvas/snap';
import { interactionPolicy } from './interactionPolicy';
import { applyFadeMask, applyLeadingTrimShift, applyShapeMask, bakeContain, clampFinite, containFit, coverCrop, isOccludingOverlay, isUserLayerId, KonvaNode, leadingTrimYOffset, MAX_PAN_ZOOM, panFromDrag, sanitizeTextInput, sceneToKonva, textFontSpecs, textOverlayStyle, traceMaskPath } from './sceneToKonva';
import { ElementTransform, Scene, SceneTransforms, SceneValues } from './types';

// Touch devices get grabbable Transformer anchors: a slightly larger white box
// plus an enlarged invisible hit region per anchor (a fingertip covers ~40px;
// the 10px default is mouse-sized). Evaluated once — pointer class doesn't
// change mid-session, and SSR has no window.
const COARSE_POINTER = typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;
// SCREEN px. Konva's Transformer ignores ancestor transforms by design — its
// anchor geometry is in absolute (screen) pixels no matter what the Stage
// scale is, so these are used as-is. (Dividing by the stage scale here renders
// 47px anchors and puts the rotate handle 150px above the element, off-board.)
const TOUCH_ANCHOR_PX = 16;
const TOUCH_ANCHOR_HIT_PAD_PX = 12;
const ROTATE_ANCHOR_OFFSET_PX = 50; // Konva's default
/** Smallest box a Transformer may produce, in RENDERED px. */
const MIN_BOX_PX = 5;
// Pads each anchor's hit area (the visual stays anchorSize) — rotate handle included.
const padAnchorHitArea = (anchor: any, pad: number) => {
  anchor.hitFunc((ctx: any, shape: any) => {
    ctx.beginPath();
    ctx.rect(-pad, -pad, shape.width() + pad * 2, shape.height() + pad * 2);
    ctx.closePath();
    ctx.fillStrokeShape(shape);
  });
};

interface EditProps {
  ref?: (n: any) => void;
  draggable?: boolean;
  listening?: boolean;
  preventDefault?: boolean;
  onClick?: () => void;
  onTap?: () => void;
  onDblClick?: () => void;
  onDblTap?: () => void;
  onDragStart?: (e: any) => void;
  onDragEnd?: (e: any) => void;
  onTransformStart?: (e: any) => void;
  onTransform?: (e: any) => void;
  onTransformEnd?: (e: any) => void;
  dragBoundFunc?: (pos: { x: number; y: number }) => { x: number; y: number };
}

function SceneImage({ node, editProps, onLoad, scale = 1, panMode = false, onPan }: {
  node: KonvaNode; editProps: EditProps; onLoad?: () => void;
  scale?: number; panMode?: boolean; onPan?: (pan: { x: number; y: number; zoom: number }) => void;
}) {
  const { _scaleMode, _fit, _fadeMask, _shapeMask, _imagePan, width, height, x, y, ...rest } = node.props as Record<string, unknown>;
  const [img, setImg] = React.useState<HTMLImageElement | null>(null);
  const startPanRef = React.useRef({ x: 0, y: 0 });
  // Drag-to-pan / wheel-to-zoom coalesce to one re-bake per animation frame
  // (Konva fires the drag-bound on every pointer move and wheel events stream;
  // a full mask re-bake each tick janks).
  const panRafRef = React.useRef(0);
  const pendingPanRef = React.useRef<{ x: number; y: number; zoom: number } | null>(null);
  React.useEffect(() => () => { if (panRafRef.current) cancelAnimationFrame(panRafRef.current); }, []);

  React.useEffect(() => {
    if (!node.imageSrc) {
      onLoad?.();
      return;
    }
    let cancelled = false;
    const image = new window.Image();
    // NOTE: NO crossOrigin here. The editor's Konva canvas is preview-only —
    // export is server-side (Konva-on-Node in the Lambda / generator
    // container), which has its own non-CORS fetcher. Requesting CORS in the
    // browser would block images from buckets that don't return
    // Access-Control-Allow-Origin (most of our prod S3 buckets), which used to
    // work fine because the editor never read the rendered bitmap back.
    image.onload = () => {
      // Guard against the "broken state" crash: an HTMLImageElement whose
      // load failed (404, decode error, ANY non-image content like a video
      // URL we tried to load) ends up with naturalWidth === 0. Konva's
      // ctx.drawImage(brokenImg) throws — never hand a broken image to the
      // stage. We still call onLoad so the parent's image-counter advances.
      if (cancelled) return;
      if (image.naturalWidth > 0 && image.naturalHeight > 0) setImg(image);
      onLoad?.();
    };
    image.onerror = () => {
      if (cancelled) return;
      // Keep going — the element just won't render. Logged so it's visible.
      console.warn(`[SceneImage] failed to load image: ${node.imageSrc}`);
      onLoad?.();
    };
    image.src = node.imageSrc;
    return () => {
      cancelled = true;
      image.onload = null;
      image.onerror = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.imageSrc]);

  // Bake the fade mask (PSD gradient-mask import) into the bitmap once per
  // image/geometry change. For cover (FILL) the faded canvas IS the cropped
  // region, so Konva's crop must NOT be applied on top of it.
  const fade = _fadeMask as { direction: 'down' | 'up' | 'left' | 'right'; start: number; end: number } | undefined;
  // USER-REPLACED image (deriveImageFit): contain inside the box. Checked
  // before the scaleMode branches below — but never over a shape mask, whose
  // baked bitmap already positions the image (sceneToKonva never sets _fit there).
  const containMode = _fit === 'contain' && !_shapeMask;

  // CONTAIN FIRST, then fade (below). The fade gradient is defined over the
  // ELEMENT BOX, so it has to be applied to the box-sized bitmap: fading the
  // source and containing the result compresses the whole ramp into the
  // letterboxed rect and leaves the margins un-faded.
  const contained = React.useMemo(() => {
    if (!img || !containMode) return null;
    return bakeContain(img, img.naturalWidth, img.naturalHeight, width as number, height as number);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [img, containMode, width, height]);

  const faded = React.useMemo(() => {
    if (!img || !fade) return null;
    // Contained: the bitmap already IS the box, so the gradient spans it 1:1.
    if (containMode) {
      return contained
        ? applyFadeMask(contained, contained.width, contained.height, null, fade)
        : null;
    }
    // FIT draws the WHOLE image — there is no cover crop to bake in.
    const crop = _scaleMode === 'FIT' ? null : coverCrop(img.naturalWidth, img.naturalHeight, width as number, height as number);
    return applyFadeMask(img, img.naturalWidth, img.naturalHeight, crop, fade);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [img, fade, _scaleMode, containMode, contained, width, height]);

  // Bake the vector clip mask (ellipse/star/path) into a BOX-sized bitmap: the
  // image is positioned in the box per scaleMode, then clipped to the path. The
  // result fills the element box directly — no Konva crop or containFit.
  const shape = _shapeMask as { path: string; fillRule?: 'nonzero' | 'evenodd' } | undefined;
  const pan = _imagePan as { x: number; y: number; zoom?: number } | undefined;
  const panX = pan?.x ?? 0;
  const panY = pan?.y ?? 0;
  const panZoom = pan?.zoom ?? 1;
  const masked = React.useMemo(() => {
    if (!img || !shape) return null;
    return applyShapeMask(img, img.naturalWidth, img.naturalHeight, width as number, height as number, _scaleMode as string | undefined, shape, { x: panX, y: panY, zoom: panZoom });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [img, shape, _scaleMode, width, height, panX, panY, panZoom]);

  if (img && masked) {
    // Drag-to-pan: in reposition mode the drag PANS the source inside the fixed
    // clip (collage framing) instead of moving the element. dragBoundFunc pins
    // the node to its box and converts the attempted drag into a pan offset.
    const flushPan = () => {
      panRafRef.current = 0;
      if (pendingPanRef.current) { onPan!(pendingPanRef.current); pendingPanRef.current = null; }
    };
    const panProps = panMode && onPan
      ? {
          draggable: true,
          onDragStart: () => { startPanRef.current = { x: panX, y: panY }; },
          onDragEnd: () => { if (panRafRef.current) { cancelAnimationFrame(panRafRef.current); } flushPan(); },
          dragBoundFunc: (pos: { x: number; y: number }) => {
            const pinned = { x: (x as number) * scale, y: (y as number) * scale };
            // Compute the new pan every tick (cheap) but coalesce the costly
            // re-bake (setState → re-render) to one per frame. Keep the current
            // zoom so the partial transform we persist stays consistent.
            pendingPanRef.current = { ...panFromDrag(
              startPanRef.current,
              (pos.x - pinned.x) / scale,
              (pos.y - pinned.y) / scale,
              img.naturalWidth, img.naturalHeight, width as number, height as number, panZoom,
            ), zoom: panZoom };
            if (!panRafRef.current) panRafRef.current = requestAnimationFrame(flushPan);
            return pinned;
          },
          // Wheel/trackpad = zoom the source inside the cell (collage crop).
          // Accumulate against the freshest pending value so a fast scroll burst
          // compounds correctly before the re-render lands.
          // (Mobile pinch-zoom is NOT handled here: node-level touch handlers
          // only fire when both fingers land on this node, which in Fill Mode
          // the markers overlay mostly prevents. The renderer owns pinch via
          // window-level listeners during the reposition lock — see the
          // pinch-to-scale effect in SceneRenderer.)
          onWheel: (e: any) => {
            e.evt.preventDefault();
            const cur = pendingPanRef.current;
            const curZoom = cur?.zoom ?? panZoom;
            const next = clampFinite(curZoom * Math.exp(-e.evt.deltaY * 0.0015), 1, MAX_PAN_ZOOM, 1);
            pendingPanRef.current = { x: cur?.x ?? panX, y: cur?.y ?? panY, zoom: next };
            if (!panRafRef.current) panRafRef.current = requestAnimationFrame(flushPan);
          },
        }
      : {};
    return (
      <KonvaImage
        {...rest}
        x={x as number}
        y={y as number}
        width={width as number}
        height={height as number}
        image={masked}
        {...editProps}
        {...panProps}
      />
    );
  }

  if (img && contained) {
    // Geometry is the BOX, deliberately: the baked bitmap already holds the
    // contained image, so a drag/resize edits the box and re-containing is a
    // pure function of it. `faded` here is that same box-sized bitmap with the
    // gradient applied over it.
    return (
      <KonvaImage
        {...rest}
        x={x as number}
        y={y as number}
        width={width as number}
        height={height as number}
        image={faded ?? contained}
        {...editProps}
      />
    );
  }

  if (img && _scaleMode === 'FIT') {
    const box = containFit(img.naturalWidth, img.naturalHeight, width as number, height as number, x as number, y as number);
    return <KonvaImage {...rest} {...box} image={faded ?? img} {...editProps} />;
  }
  const crop = img && !faded ? coverCrop(img.naturalWidth, img.naturalHeight, width as number, height as number) : undefined;
  return (
    <KonvaImage
      {...rest}
      x={x as number}
      y={y as number}
      width={width as number}
      height={height as number}
      image={faded ?? img ?? undefined}
      crop={crop}
      {...editProps}
    />
  );
}

/** Per-text-input constraints for inline editing (derived from template.inputs). */
export interface TextEditMeta {
  editable: boolean;
  maxChars?: number;
  multiline?: boolean;
}

/** Admin editing mode. Presence of this prop is what widens every interaction
 *  gate (see interactionPolicy); absence leaves end-user behaviour untouched. */
export interface SceneAdminMode {
  /** Pull a dragged element's centre onto the board centre lines. Alt bypasses
   *  it live, per event. */
  snapToCenter: boolean;
  /** elementId -> ids that must travel with it during a gesture: the synthetic
   *  tint/gradient overlays the importer emits with the SAME sourceNodeId. The
   *  live follow here is PREVIEW only; the committed values are mirrored by the
   *  session hook (useAdminBoardSession) and, on save, by the server, which
   *  applies one delta to every element sharing the sourceNodeId. */
  linkedElements?: Map<string, string[]>;
  /** Fired when the snapped-axis set changes, so the editor can mirror the
   *  state outside the canvas. The guide lines themselves are drawn here. */
  onSnapGuides?: (guides: { x: boolean; y: boolean }) => void;
}

export interface SceneEditorHandle {
  /** Close any open inline text edit and RETURN what it committed, because the
   *  matching state update lands asynchronously and cannot be read back in the
   *  same tick by the caller that is about to build a request body. */
  flushTextEdit: () => { inputKey: string; value: string } | null;
  /** Open the inline editor on an element the PAGE chose (the "ערוך טקסט"
   *  button, the Enter key). Optional so existing consumers — and the test
   *  doubles that mock this handle — keep type-checking.
   *  Returns false when the element is not an editable text. */
  enterTextEdit?: (elementId: string) => boolean;
}

export interface SceneRendererProps {
  scene: Scene;
  values?: SceneValues;
  transforms?: SceneTransforms;
  /** Per-inputKey fit override for USER-REPLACED images (deriveImageFit in
   *  ./imageFit): 'contain' letterboxes inside the field box instead of
   *  cover-cropping. Sent to the export renderer as `imageFit` too, so the
   *  preview and the generated PNG/MP4 agree. */
  imageFit?: { [inputKey: string]: string };
  scale?: number;
  onReady?: () => void;
  // --- editor mode ---
  editable?: boolean;
  selectedId?: string | null;
  onSelect?: (id: string | null) => void;
  onTransformChange?: (id: string, t: ElementTransform) => void;
  /** Reposition lock: when true, ONLY `selectedId` is interactive (draggable +
   *  listening) — drag it even if hidden behind other layers, and clicks can't
   *  switch selection. When false (default) elements are freely click-selected/dragged. */
  repositionLock?: boolean;
  /** Show the dashed editable-text discoverability hints (default true). Fill
   *  Mode turns these off — its own marker overlay already flags every field,
   *  so the built-in hints would double-mark each text. */
  showTextHints?: boolean;
  // --- inline text-value editing ---
  // END USER: a single click on an editable text opens it.
  // ADMIN: a click only selects; the editor opens on double-click, on Enter, or
  // through `SceneEditorHandle.enterTextEdit`.
  onTextChange?: (inputKey: string, value: string) => void;
  textMeta?: (inputKey: string) => TextEditMeta | null;
  /** Single-click handler for IMAGE elements — opens the parent's asset
   *  selection gallery. Matches the legacy editor: tapping an editable image
   *  on the canvas swaps the asset; only the form-panel Reposition button
   *  enters the transform-handles flow (via `repositionLock`). */
  onImageInputClick?: (inputKey: string) => void;
  /** Single-click handler for editable TEXT elements. When provided it REPLACES
   *  the on-canvas inline editor: the click reports the input key and the parent
   *  decides what to open. Fill Mode passes it so a tap anywhere on the glyphs
   *  raises the top text sheet — the phone has no room for an on-canvas textarea
   *  under the keyboard, and the sheet is what the checklist chips open too.
   *  Desktop and the admin editors omit it and keep inline editing. */
  onTextInputClick?: (inputKey: string) => void;
  /** Template fonts to register (@font-face) + preload before drawing text. */
  fonts?: Record<string, { urls?: string[] | null } | null>;
  /** Admin editing mode — off (undefined) for every end-user surface. */
  admin?: SceneAdminMode;
  /** Imperative handle, so a Save can commit an OPEN inline text edit BEFORE it
   *  reads the session. Relying on the textarea's blur firing first is a
   *  browser-ordering bet, and losing it silently drops the admin's last edit. */
  editorHandleRef?: React.MutableRefObject<SceneEditorHandle | null>;
  /** Inline text editing OPENED on this input. Additive: end-user callers don't
   *  pass it. */
  onTextEditStart?: (inputKey: string) => void;
  /** Inline text editing CLOSED (blur/Escape/Enter/selection change) with its
   *  final value. `onTextChange` fires per keystroke, which is the wrong grain
   *  for an undo history — this fires exactly once per edit. */
  onTextEditCommit?: (inputKey: string, value: string) => void;
  /** ROW reposition (end user only; ignored in admin): element ids that move
   *  together as one unit. Non-empty = group mode — a stricter reposition lock
   *  in which ONLY these nodes listen, one Transformer frames all of them with
   *  no resize/rotate handles, and dragging anywhere inside that frame moves
   *  the whole set. Decoration inside the row (its bar, icons) moves too, which
   *  is why membership bypasses the normal selectability gate. */
  groupIds?: readonly string[] | null;
  /** Fired ONCE per group drag, at its end, with the delta in SCENE units. The
   *  parent applies it to every member (see scene/rowReposition). */
  onGroupMove?: (dx: number, dy: number) => void;
}

/** The element a multi-shadow CLONE belongs to. sceneToKonva emits a clone per
 *  extra shadow with NO elementId and `props.id = <element id>__sh<k>`, drawn
 *  right behind the real node — so it has to travel with it on a group drag. */
const shadowCloneOwner = (node: KonvaNode): string | null => {
  if (node.elementId) return null;
  const match = /^(.+)__sh\d+$/.exec(String(node.props.id ?? ''));
  return match ? match[1] : null;
};

export function SceneRenderer({
  scene,
  values = {},
  transforms = {},
  imageFit,
  scale = 1,
  onReady,
  editable = false,
  selectedId = null,
  onSelect,
  onTransformChange,
  onTextChange,
  textMeta,
  onImageInputClick,
  onTextInputClick,
  fonts,
  repositionLock = false,
  showTextHints = true,
  admin,
  onTextEditStart,
  onTextEditCommit,
  editorHandleRef,
  groupIds,
  onGroupMove,
}: SceneRendererProps) {
  // Editor preview prefers asset thumbnails over full-res highRes (less wire
  // traffic, faster updates while typing). The server-side export path runs
  // its own sceneToKonva call WITHOUT this flag, so the exported PNG/MP4 still
  // composites the full-resolution asset.
  const nodes = sceneToKonva(scene, values, transforms, { preferThumbnail: true, imageFit });
  const imageCount = nodes.filter((n) => n.konvaType === 'Image').length;
  // Row reposition. The admin board has no rows to move (it edits the design
  // itself), so the mode is end-user only.
  const groupSet = React.useMemo(() => new Set(groupIds ?? []), [groupIds]);
  const groupMode = editable && !admin && groupSet.size > 0;
  /** Is this node part of the group being moved (a member or its shadow clone)? */
  const inGroup = (node: KonvaNode): boolean =>
    groupMode && groupSet.has(node.elementId ?? shadowCloneOwner(node) ?? '');
  // Both locks share the "only the target is live" semantics below.
  const locked = repositionLock || groupMode;
  /** The node(s) a lock leaves interactive. */
  const isLockTarget = (node: KonvaNode): boolean =>
    groupMode ? !!node.elementId && groupSet.has(node.elementId) : node.elementId === selectedId;
  // Full-board images stacked above smaller image content (a collage frame /
  // foreground overlay) are NOT user-editable and must not catch selection
  // clicks — combined with alpha-aware hit (SceneImage) the cells beneath stay
  // selectable through the overlay's transparent areas.
  // User layers are exempt: isOccludingOverlay already refuses to flag one, and
  // this second gate keeps that guarantee even if a caller passes ids in.
  const occludingIds = new Set(
    nodes
      .filter((n, i) => !!n.elementId && !isUserLayerId(n.elementId)
        && isOccludingOverlay(n, nodes.slice(0, i), scene.width, scene.height))
      .map((n) => n.elementId as string),
  );
  // EVERY interaction gate below reads this one object. Deriving it once is the
  // point: the end-user and admin rule sets differ in nine places, and an
  // independent `admin ?` at each site is how one of them gets missed.
  const policy = interactionPolicy({ admin: !!admin, occludingIds, textMeta });
  // Ids of selectable nodes — used to decide deselect on background click.
  const editableIds = new Set(
    nodes.filter((n) => policy.selectable(n)).map((n) => n.elementId as string),
  );
  const loaded = React.useRef(0);
  const [fontsReady, setFontsReady] = React.useState(false);
  const nodeRefs = React.useRef<Map<string, any>>(new Map());
  /** Multi-shadow clone nodes, by their `<element id>__sh<k>` Konva id. */
  const shadowRefs = React.useRef<Map<string, { owner: string; node: any }>>(new Map());
  const trRef = React.useRef<any>(null);
  // Element id currently being text-edited via the HTML <textarea> overlay.
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const editingNode = editingId ? nodes.find((n) => n.elementId === editingId) ?? null : null;
  // The open textarea's live value and the input key it belongs to. Kept in refs
  // so that EVERY route out of edit mode (blur, Escape, Enter, selection change,
  // scene swap) can commit exactly one history entry — reading them off the DOM
  // at close time is unreliable, the node may already be gone.
  const editingValueRef = React.useRef<{ inputKey: string; value: string } | null>(null);
  const onTextEditCommitRef = React.useRef(onTextEditCommit);
  onTextEditCommitRef.current = onTextEditCommit;
  /** The ONE way out of inline editing. Fires onTextEditCommit once. */
  const closeEdit = React.useCallback((): { inputKey: string; value: string } | null => {
    const pending = editingValueRef.current;
    editingValueRef.current = null;
    setEditingId(null);
    if (pending) onTextEditCommitRef.current?.(pending.inputKey, pending.value);
    return pending;
  }, []);

  // Latest-value refs so the handle can stay mounted for the ref's lifetime
  // instead of being rebuilt (and momentarily null) on every render.
  const enterEditRef = React.useRef<(node: KonvaNode) => void>(() => undefined);
  const policyRef = React.useRef(policy);
  policyRef.current = policy;
  React.useEffect(() => {
    if (!editorHandleRef) return undefined;
    editorHandleRef.current = {
      flushTextEdit: closeEdit,
      enterTextEdit: (elementId: string) => {
        const target = nodesRef.current.find((n) => n.elementId === elementId);
        // The SAME predicate the canvas click path uses — the page must not be
        // able to open an editor on something the policy would refuse.
        if (!target || !policyRef.current.inlineTextEditable(target)) return false;
        enterEditRef.current(target);
        return true;
      },
    };
    return () => {
      editorHandleRef.current = null;
    };
  }, [editorHandleRef, closeEdit]);
  // If the selected/edited element disappears (scene swap), drop the overlay.
  React.useEffect(() => {
    if (editingId && !nodes.some((n) => n.elementId === editingId)) closeEdit();
  }, [scene, editingId, nodes, closeEdit]);
  // Selecting / transforming a different element exits text-edit cleanly — and
  // so does CLEARING the selection, which the admin editors now offer as an
  // explicit "בטל בחירה". The null case is guarded on the previous value
  // instead of just `selectedId !== editingId`, because entering an edit sets
  // `editingId` and only THEN asks the parent to select it: for one render
  // `selectedId` is legitimately not the edited element, and an unguarded rule
  // would slam the editor shut the moment it opened (or permanently, for a
  // consumer that tracks no selection at all).
  const previousSelectedIdRef = React.useRef(selectedId);
  React.useEffect(() => {
    const previous = previousSelectedIdRef.current;
    previousSelectedIdRef.current = selectedId;
    if (!editingId) return;
    const movedAwayFromTheEdit = previous === editingId && selectedId !== editingId;
    const switchedToAnother = !!selectedId && selectedId !== editingId;
    if (movedAwayFromTheEdit || switchedToAnother) closeEdit();
  }, [selectedId, editingId, closeEdit]);
  // Escape deselects (and exits text-edit). A reliable deselect even when full-bleed
  // editable layers cover the whole board (so a background click never lands on a
  // non-editable node). Mobile uses the tap-outside-the-board handler in TemplateDisplay.
  // Read through a ref (assigned below, once groupDragging exists) so the
  // listener needn't re-subscribe on every render.
  const groupDragInProgressRef = React.useRef<() => boolean>(() => false);
  React.useEffect(() => {
    if (!editable) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Not mid row-drag: ending row mode now would unmount the members'
      // dragend handlers, so the drop would never reach sceneTransforms and
      // the canvas would disagree with the draft/export.
      if (groupDragInProgressRef.current()) return;
      if (editingId) closeEdit();
      else onSelect?.(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editable, editingId, onSelect, closeEdit]);

  const enterEdit = (node: KonvaNode) => {
    if (!policy.inlineTextEditable(node) || !node.elementId || !node.inputKey) return;
    onSelect?.(node.elementId);
    editingValueRef.current = { inputKey: node.inputKey, value: String(node.props.text ?? '') };
    setEditingId(node.elementId);
    onTextEditStart?.(node.inputKey);
  };
  enterEditRef.current = enterEdit;

  // A new scene is a new load: restart the image count DURING render. As an
  // effect it ran after the children's (React runs child effects first), so an
  // image that reported loaded while mounting was counted and then wiped.
  const countedFor = React.useRef<Scene | null>(null);
  if (countedFor.current !== scene) {
    countedFor.current = scene;
    loaded.current = 0;
  }

  React.useEffect(() => {
    let cancelled = false;
    // Register @font-face here (not just in the parent) so it exists before we
    // preload — child effects run before the parent's. Then force the actual
    // download so Konva re-measures text in the real font (see ensureSceneFontsLoaded).
    registerSceneFonts(fonts);
    ensureSceneFontsLoaded(textFontSpecs(nodes)).then(() => !cancelled && setFontsReady(true));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, fonts]);

  // A shape-masked (collage) image repositions by PANNING the source inside its
  // fixed clip — not by move/resize — so it gets no Transformer handles. The
  // rule (cover-FILL `_imagePan`, unrotated, end-user only) lives in the policy:
  // admin mode moves the element BOX and must bypass the whole pan family.
  const isPannableImage = (node: KonvaNode | undefined | null): boolean =>
    !!node && policy.pannable(node);

  // Pinch-to-zoom/scale during a reposition lock — WINDOW-level capture
  // listeners, deliberately NOT Konva node handlers: node touch events only
  // fire when both fingers land inside the selected element's box, and in Fill
  // Mode the markers overlay (cancel strips + switch hit-areas) covers most of
  // the board, so on a phone at least one finger almost always lands off-node
  // and Safari runs its native page zoom instead. The window sees every touch
  // regardless of target.
  //   pannable (collage) image → panZoom, same clamp as the wheel path;
  //   regular image            → scale the element about its center (what the
  //                              Transformer's corner handles do on desktop).
  // Latest nodes/handler via refs: a transform re-render mid-gesture rebuilds
  // `nodes`, and re-running the effect then would drop the live pinch state.
  const nodesRef = React.useRef(nodes);
  nodesRef.current = nodes;
  const onTransformChangeRef = React.useRef(onTransformChange);
  onTransformChangeRef.current = onTransformChange;
  React.useEffect(() => {
    // Admin mode moves the element BOX and has no pan/pinch family at all —
    // it never sets repositionLock either, but the gate is stated explicitly so
    // the policy's coverage is verifiable rather than incidental.
    // A row moves as a unit and has no pinch — it must not scale whatever
    // `selectedId` happens to still name.
    if (!editable || !repositionLock || !selectedId || admin || groupMode) return;

    let startDist = 0;
    let pinching = false;
    let pinchRaf = 0;
    // Regular-element gesture snapshot (stage units, axis-aligned or rotated).
    let box: { x: number; y: number; w: number; h: number; rotDeg: number } | null = null;
    let startZoom = 1;
    let pendingRatio = 1;

    const selNode = () => nodesRef.current.find((n) => n.elementId === selectedId);
    const konvaSel = () => nodeRefs.current.get(selectedId);
    const dist = (t: TouchList) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

    const apply = () => {
      pinchRaf = 0;
      const node = selNode();
      const n = konvaSel();
      if (!node || !n) return;
      if (isPannableImage(node)) {
        const pan = ((node.props as Record<string, unknown>)._imagePan ?? {}) as { x?: number; y?: number; zoom?: number };
        const zoom = clampFinite(startZoom * pendingRatio, 1, MAX_PAN_ZOOM, 1);
        onTransformChangeRef.current?.(selectedId, { panX: pan.x ?? 0, panY: pan.y ?? 0, panZoom: zoom });
        return;
      }
      if (!box) return;
      // Scale about the element center; with rotation the top-left orbits the
      // center along the rotated half-diagonal.
      const r = pendingRatio;
      const rad = (box.rotDeg * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      const cx = box.x + (box.w / 2) * cos - (box.h / 2) * sin;
      const cy = box.y + (box.w / 2) * sin + (box.h / 2) * cos;
      n.scaleX(r);
      n.scaleY(r);
      n.position({
        x: cx - ((box.w * r) / 2) * cos + ((box.h * r) / 2) * sin,
        y: cy - ((box.w * r) / 2) * sin - ((box.h * r) / 2) * cos,
      });
      n.getLayer()?.batchDraw();
    };

    const persist = () => {
      const node = selNode();
      const n = konvaSel();
      if (!node || !n || isPannableImage(node) || !box) return;
      // Mirror onTransformEnd: bake the visual scale into w/h and reset it.
      // (Images only — no leading-trim y compensation needed here.)
      const sx = n.scaleX();
      n.scaleX(1);
      n.scaleY(1);
      onTransformChangeRef.current?.(selectedId, {
        x: n.x(),
        y: n.y(),
        w: Math.max(5, box.w * sx),
        h: Math.max(5, box.h * sx),
      });
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 2) return;
      const node = selNode();
      const n = konvaSel();
      if (!node || !n) return;
      // Text pinch-resize is out of scope (box w/h without fontSize just
      // re-wraps); the gesture is for images.
      if (node.konvaType !== 'Image') return;
      if (e.cancelable) e.preventDefault(); // beat Safari's native page zoom
      // A one-finger drag may be mid-flight (including the marker long-press
      // relay drag) — end it so the pan/move doesn't fight the zoom.
      if (n.isDragging?.()) n.stopDrag();
      startDist = dist(e.touches);
      pendingRatio = 1;
      pinching = startDist > 0;
      if (isPannableImage(node)) {
        const pan = ((node.props as Record<string, unknown>)._imagePan ?? {}) as { zoom?: number };
        startZoom = pan.zoom ?? 1;
        box = null;
      } else {
        box = { x: n.x(), y: n.y(), w: n.width(), h: n.height(), rotDeg: n.rotation?.() ?? 0 };
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!pinching || e.touches.length !== 2) return;
      if (e.cancelable) e.preventDefault();
      pendingRatio = clampFinite(dist(e.touches) / startDist, 0.1, 20, 1);
      if (!pinchRaf) pinchRaf = requestAnimationFrame(apply);
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!pinching || e.touches.length >= 2) return;
      // Suppress the compat mouse events so the lift doesn't "click" a cancel
      // strip and drop the selection the user is mid-adjusting.
      if (e.cancelable) e.preventDefault();
      pinching = false;
      if (pinchRaf) {
        cancelAnimationFrame(pinchRaf);
        pinchRaf = 0;
        apply();
      }
      persist();
      box = null;
    };

    window.addEventListener('touchstart', onTouchStart, { passive: false, capture: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
    window.addEventListener('touchend', onTouchEnd, { passive: false, capture: true });
    window.addEventListener('touchcancel', onTouchEnd, { passive: false, capture: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart, { capture: true } as EventListenerOptions);
      window.removeEventListener('touchmove', onTouchMove, { capture: true } as EventListenerOptions);
      window.removeEventListener('touchend', onTouchEnd, { capture: true } as EventListenerOptions);
      window.removeEventListener('touchcancel', onTouchEnd, { capture: true } as EventListenerOptions);
      if (pinchRaf) cancelAnimationFrame(pinchRaf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, repositionLock, selectedId, admin, groupMode]);

  // Handles are offered ONLY for a node the policy actually allows to be
  // transformed. `selectedId` is a controlled prop and can name anything — the
  // end-user editor resolves it from an input key with no selectability check,
  // so an occluding full-board overlay can land here. Resolving purely through
  // nodeRefs (which now holds every element, for the linked-overlay follow)
  // would hand that node a Transformer it must never get.
  const selectionAttachable = !!selectedId && editableIds.has(selectedId);
  // The React keys of the group's nodes: they change exactly when one of them
  // REMOUNTS (index shift, the fontsReady re-key of Text), which is when the
  // Transformer is left holding a destroyed node and must re-attach.
  const groupNodeKeys = groupMode
    ? nodes.map((n, i) => (inGroup(n) ? `${n.props.id}:${i}:${fontsReady}` : '')).join('|')
    : '';
  React.useEffect(() => {
    const tr = trRef.current;
    if (!tr) return;
    if (groupMode) {
      // Every member AND its shadow clones: Konva 9's Transformer drags all of
      // its attached nodes together (see Transformer._proxyDrag), and a clone
      // left out would stay behind until the drop re-renders it.
      const clones = [...shadowRefs.current.values()]
        .filter((clone) => groupSet.has(clone.owner))
        .map((clone) => clone.node);
      const members = [...groupSet].flatMap((id) => {
        const n = nodeRefs.current.get(id);
        return n ? [n] : [];
      });
      tr.nodes([...members, ...clones]);
      tr.getLayer()?.batchDraw();
      return;
    }
    const selectedNode = selectedId ? nodes.find((n) => n.elementId === selectedId) : null;
    // No transform handles while text-editing or while panning a clipped image.
    const selected = selectionAttachable && !editingId && !isPannableImage(selectedNode)
      ? nodeRefs.current.get(selectedId as string)
      : null;
    tr.nodes(selected ? [selected] : []);
    tr.getLayer()?.batchDraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selectionAttachable, editingId, nodes.length, editable, groupMode, groupNodeKeys]);

  // GROUP DRAG delta. Snapshot the members' node positions at pointer-down —
  // before Konva moves anything — and measure the delta off ONE member at the
  // first dragend. Deriving it from props instead is wrong for some nodes (a
  // leading-trimmed Text draws trimOffset above its box, a FIT image at its
  // contain rect), and a drag grabbed on the Transformer's background only
  // starts the members' own drags AFTER applying the first move, so their
  // dragstart is too late to snapshot. The same delta is right for all of
  // them: every node offset is a constant.
  const groupStartRef = React.useRef<Map<string, { x: number; y: number }> | null>(null);
  /** Is a group drag under way? Konva flags the members it drags (and the
   *  Transformer / its back, depending on where the drag was grabbed). */
  const groupDragging = (): boolean => {
    const tr = trRef.current;
    if (tr?.isDragging?.() || tr?.findOne?.('.back')?.isDragging?.()) return true;
    return [...groupSet].some((id) => !!nodeRefs.current.get(id)?.isDragging?.());
  };
  groupDragInProgressRef.current = () => groupMode && groupDragging();
  const snapshotGroup = () => {
    // A SECOND pointer-down mid-drag (another finger) must not re-snapshot:
    // the members have already moved, so the delta would lose everything
    // dragged so far. A pointer-down while idle may replace a stale snapshot
    // left by a tap that never became a drag.
    if (groupStartRef.current && groupDragging()) return;
    const start = new Map<string, { x: number; y: number }>();
    groupSet.forEach((id) => {
      const n = nodeRefs.current.get(id);
      if (n) start.set(id, { x: n.x(), y: n.y() });
    });
    groupStartRef.current = start;
  };
  const groupEditProps = (node: KonvaNode): EditProps => {
    const id = node.elementId;
    if (!id || !groupSet.has(id)) return {};
    // No click routes: in row mode a tap must not open the gallery or the
    // text editor — the only gesture is the move.
    return {
      draggable: true,
      // Fallback for a drag that never reported a pointer-down to the Stage.
      onDragStart: () => { if (!groupStartRef.current) snapshotGroup(); },
      onDragEnd: (e: any) => {
        // Every member fires dragend; the first one commits, the rest find
        // the snapshot gone.
        const start = groupStartRef.current?.get(id);
        groupStartRef.current = null;
        if (!start) return;
        const n = nodeRefs.current.get(id) ?? e.target;
        const dx = n.x() - start.x;
        const dy = n.y() - start.y;
        if (dx !== 0 || dy !== 0) onGroupMove?.(dx, dy);
      },
    };
  };

  // "Ready" is a LOAD event: fonts in AND every image decoded, whichever comes
  // last. Everything it reads goes through refs, for two reasons:
  // - the callback: TemplateEditor re-creates onTemplateLoad every render and
  //   sets state in it, so keying on it made a board with no images re-signal
  //   ready on every render, forever;
  // - the counts: SceneImage keeps the onLoad of the render its load started in,
  //   so an image that finished before the fonts saw fontsReady=false, and when
  //   the fonts arrived nothing signalled at all.
  const onReadyRef = React.useRef(onReady);
  onReadyRef.current = onReady;
  const fontsReadyRef = React.useRef(fontsReady);
  fontsReadyRef.current = fontsReady;
  const imageCountRef = React.useRef(imageCount);
  imageCountRef.current = imageCount;

  // The last image in (the fonts were already there).
  const handleImageLoad = React.useCallback(() => {
    loaded.current += 1;
    if (fontsReadyRef.current && loaded.current >= imageCountRef.current) onReadyRef.current?.();
  }, []);

  // The fonts in (the images were already there — or there are none).
  React.useEffect(() => {
    if (fontsReady && loaded.current >= imageCount) onReadyRef.current?.();
  }, [scene, fontsReady, imageCount]);

  // ------------------------------------------------------------------ admin
  // Alt is read LIVE (per event) rather than latched at drag start: pressing it
  // mid-drag has to release an already-snapped element immediately. Konva's
  // dragBoundFunc gets no event, so the modifier is tracked off the window.
  const altRef = React.useRef(false);
  React.useEffect(() => {
    if (!admin) return undefined;
    const read = (e: KeyboardEvent | MouseEvent) => { altRef.current = e.altKey; };
    window.addEventListener('keydown', read);
    window.addEventListener('keyup', read);
    window.addEventListener('mousemove', read);
    return () => {
      window.removeEventListener('keydown', read);
      window.removeEventListener('keyup', read);
      window.removeEventListener('mousemove', read);
    };
  }, [admin]);

  // GESTURE-START snapshot of the dragged node and everything linked to it.
  // Deltas are measured from here, never from the node's live position: a snap
  // applied to an already-snapped position goes sticky (each move re-snaps from
  // the snapped spot) and Alt can then never release it.
  /** The scene element with this id, at ANY depth, with the session's override
   *  applied. Konva only renders top-level elements, so a nested linked overlay
   *  has no node to measure — and a Text node's measured height is not its
   *  element box either. Both matter for the resize floor below. */
  const elementBoxOf = React.useCallback((elementId: string): { w: number; h: number } | null => {
    const find = (elements: any[] | undefined): any => {
      for (const element of elements ?? []) {
        if (element?.id === elementId) return element;
        const nested = element?.elements?.length ? find(element.elements) : null;
        if (nested) return nested;
      }
      return null;
    };
    const element = find(scene.elements as any);
    if (!element) return null;
    const override = transforms[elementId];
    return {w: override?.w ?? element.w, h: override?.h ?? element.h};
  }, [scene, transforms]);

  const gestureRef = React.useRef<{
    id: string;
    /** NODE space (a leading-trimmed text node sits trimOffset above its box). */
    start: { x: number; y: number; w: number; h: number };
    /** Canonical boxes of everything linked to the dragged element — including
     *  the nested overlays Konva never renders. Only the resize FLOOR uses
     *  these; the live follow below still needs real Konva nodes. */
    linkedBoxes: { w: number; h: number }[];
    /** The element's TRUE board box — what snapping must centre. Mixing the
     *  node's shifted y with the real height would centre CAP_HEIGHT text on
     *  something that is not the board centre. */
    snapRect: { x: number; y: number; width: number; height: number };
    linked: { id: string; x: number; y: number; w: number; h: number }[];
  } | null>(null);
  const guideXRef = React.useRef<any>(null);
  const guideYRef = React.useRef<any>(null);
  const guidesRef = React.useRef({ x: false, y: false });
  const adminRef = React.useRef(admin);
  adminRef.current = admin;

  const setGuides = (x: boolean, y: boolean) => {
    if (guidesRef.current.x === x && guidesRef.current.y === y) return;
    guidesRef.current = { x, y };
    // Toggled imperatively: a setState here would re-render the Stage on every
    // pointer move and reset the node React still believes is un-dragged.
    guideXRef.current?.visible(x);
    guideYRef.current?.visible(y);
    guideXRef.current?.getLayer()?.batchDraw();
    adminRef.current?.onSnapGuides?.({ x, y });
  };

  const beginGesture = (id: string, konvaNode: any, node: KonvaNode) => {
    const linkedIds = adminRef.current?.linkedElements?.get(id) ?? [];
    const trimOffset = trimOffsetOf(node);
    const start = {
      x: konvaNode.x(),
      y: konvaNode.y(),
      w: konvaNode.width(),
      // Konva auto-measures a Text node's height from its content; the
      // element box is what the admin is moving, so prefer the stored one.
      h: (node.props._boxH as number) || konvaNode.height(),
    };
    gestureRef.current = {
      id,
      start,
      // y + trimOffset is the canonical element y (what transforms store).
      snapRect: {x: start.x, y: start.y + trimOffset, width: start.w, height: start.h},
      linkedBoxes: linkedIds.flatMap((linkedId) => {
        const box = elementBoxOf(linkedId);
        return box ? [box] : [];
      }),
      linked: linkedIds.flatMap((linkedId) => {
        const n = nodeRefs.current.get(linkedId);
        return n ? [{ id: linkedId, x: n.x(), y: n.y(), w: n.width(), h: n.height() }] : [];
      }),
    };
  };

  const endGesture = () => {
    gestureRef.current = null;
    setGuides(false, false);
  };

  /** Live PREVIEW only: move/resize the synthetic overlays that share the
   *  dragged element's sourceNodeId by the gesture delta. Never assign the
   *  dragged node's absolute geometry — "exact copy" only holds when the
   *  overlay started identical, and a gradient strip over an image does not.
   *  The committed values come from the session hook (which mirrors the same
   *  delta) and, after a save, from the server. */
  const followLinked = (dx: number, dy: number, dw: number, dh: number) => {
    const gesture = gestureRef.current;
    if (!gesture) return;
    let layer: any = null;
    for (const linked of gesture.linked) {
      const n = nodeRefs.current.get(linked.id);
      if (!n) continue;
      n.position({ x: linked.x + dx, y: linked.y + dy });
      if (dw !== 0 || dh !== 0) {
        n.scaleX(1);
        n.scaleY(1);
        n.width(Math.max(1, linked.w + dw));
        n.height(Math.max(1, linked.h + dh));
      }
      layer = layer ?? n.getLayer();
    }
    layer?.batchDraw();
  };

  /** Smallest box (RENDERED px) this gesture may shrink to.
   *
   *  A linked overlay receives the same dw/dh as the element that moved, so a
   *  100px image shrunk to 5px would drive a 2px gradient strip to -93px — an
   *  inverted box that the server rejects and that nothing on screen explains.
   *  The floor is raised so the smallest linked element stays at least 1 unit
   *  wide/tall, which makes the invalid delta unauthorable rather than merely
   *  refused after the fact. */
  const adminMinBox = (): { width: number; height: number } => {
    const gesture = gestureRef.current;
    const floor = {width: MIN_BOX_PX, height: MIN_BOX_PX};
    if (!policy.admin || !gesture) return floor;
    for (const linked of gesture.linkedBoxes) {
      floor.width = Math.max(floor.width, (gesture.start.w - linked.w + 1) * scale);
      floor.height = Math.max(floor.height, (gesture.start.h - linked.h + 1) * scale);
    }
    return floor;
  };

  /** Konva hands the PROPOSED absolute position; snapping rewrites it before
   *  the node ever paints there, so the element never jitters past the guide. */
  const adminDragBound = (pos: { x: number; y: number }) => {
    const gesture = gestureRef.current;
    if (!gesture || !scale) return pos;
    const rawDx = pos.x - gesture.start.x * scale;
    const rawDy = pos.y - gesture.start.y * scale;
    const snapped = computeSnap({
      // Both spaces differ by a constant (the trim offset), so the DELTA the
      // snap produces applies unchanged to the node position below.
      rect: gesture.snapRect,
      boardWidth: scene.width,
      boardHeight: scene.height,
      rawDx,
      rawDy,
      scale,
      snapEnabled: !!adminRef.current?.snapToCenter,
      altKey: altRef.current,
    });
    setGuides(snapped.snappedX, snapped.snappedY);
    followLinked(snapped.dx, snapped.dy, 0, 0);
    return { x: (gesture.start.x + snapped.dx) * scale, y: (gesture.start.y + snapped.dy) * scale };
  };

  const makeEditProps = (node: KonvaNode): EditProps => {
    if (!editable || !node.elementId) return {};
    // Row mode replaces the whole selectable/click/drag contract below.
    if (groupMode) return groupEditProps(node);
    if (!policy.selectable(node)) return {};
    // Occluding full-board overlay (collage frame/foreground): not editable, so
    // a click on its opaque parts deselects rather than opening its gallery.
    if (policy.inert(node)) return {};
    const id = node.elementId;
    // Reposition lock: only the targeted element is interactive; every other node is
    // inert (render also sets listening:false) so the drag passes through to the
    // target even when it's hidden, and clicks can't switch selection.
    if (repositionLock && id !== selectedId) return {};

    // Click routing — matches the legacy editor:
    //   IMAGE input  → first click opens the asset gallery (onImageInputClick).
    //                  Selection / transform happens only via the Reposition
    //                  button in the form panel (which sets repositionLock).
    //   TEXT  input  → `onTextInputClick` when the parent supplied one (Fill
    //                  Mode's top sheet), otherwise inline edit (textarea overlay).
    //   ANYTHING ELSE → no-op (non-editable elements aren't clickable).
    // In ADMIN mode an image click SELECTS instead (there is no gallery to
    // open — the admin is arranging the design, not filling it).
    const editsText = policy.inlineTextEditable(node);
    const handleClick = () => {
      if (policy.imageClickOpensGallery(node) && onImageInputClick) {
        onImageInputClick(node.inputKey!);
        return;
      }
      // ADMIN: a click only ever SELECTS. Opening the text editor on the same
      // click made every attempt to move a text fight a textarea for the
      // gesture; editing is now an explicit act — the double-click below, the
      // Enter key, or the page's "ערוך טקסט" button.
      if (editsText && !policy.admin) {
        // The parent owns the text-editing surface when it says so. Checked
        // BEFORE enterEdit so the two can never both open.
        if (onTextInputClick && node.inputKey) {
          onTextInputClick(node.inputKey);
          return;
        }
        enterEdit(node);
        return;
      }
      onSelect?.(id);
    };
    // Admin's second way in. End-user keeps single click, so this is inert
    // there (the click has already opened the editor).
    const handleDoubleClick = () => {
      if (editsText && policy.admin) enterEdit(node);
    };

    return {
      // Free-mode drag is gated on selectedId, which is set ONLY by the
      // form-panel Reposition button (repositionLock=true at that point). A
      // bare canvas click won't make anything draggable here — that's the
      // contract the legacy editor enforced via separate UI affordances.
      // ADMIN: every selectable element is draggable straight away, so moving
      // one is a single gesture (dragstart selects it).
      draggable: (policy.admin ? true : id === selectedId) && editingId !== id,
      // During a reposition lock, clicks must NOT trigger any of the routes
      // above (the locked element keeps its selection until Apply/Reset/Close).
      ...(repositionLock ? {} : { onClick: handleClick, onTap: handleClick }),
      // Double-click survives the lock on purpose: with a text selected, the
      // lock is exactly the state you are in when you want to edit it.
      ...(policy.admin ? { onDblClick: handleDoubleClick, onDblTap: handleDoubleClick } : {}),
      ...(policy.admin
        ? {
            onDragStart: (e: any) => {
              if (id !== selectedId) onSelect?.(id);
              beginGesture(id, e.target, node);
            },
            dragBoundFunc: adminDragBound,
            onTransformStart: (e: any) => beginGesture(id, e.target, node),
            onTransform: (e: any) => {
              const gesture = gestureRef.current;
              if (!gesture) return;
              const n = e.target;
              // Scale the SNAPSHOT box, never n.height(): for a Text node that
              // is Konva's measured line height, which is not the element box
              // the delta is expressed in.
              followLinked(
                n.x() - gesture.start.x,
                n.y() - gesture.start.y,
                gesture.start.w * n.scaleX() - gesture.start.w,
                gesture.start.h * n.scaleY() - gesture.start.h,
              );
            },
          }
        : {}),
      // Leading-trimmed text renders SHIFTED up by trimOffsetOf(node); the
      // canonical element y (what transforms store) is the trimmed-box top,
      // so add the shift back when persisting a drag/resize.
      onDragEnd: (e: any) => {
        endGesture();
        onTransformChange?.(id, { x: e.target.x(), y: e.target.y() + trimOffsetOf(node) });
      },
      onTransformEnd: (e: any) => {
        const n = e.target;
        const sx = n.scaleX();
        const sy = n.scaleY();
        n.scaleX(1);
        n.scaleY(1);
        const gesture = gestureRef.current;
        endGesture();
        // ADMIN: scale the gesture's SNAPSHOT box. n.height() on a Text node is
        // Konva's measured line height, so committing it would silently replace
        // the canonical Figma box height on a horizontal-only resize.
        const box = policy.admin && gesture?.id === id ? gesture.start : null;
        onTransformChange?.(id, {
          x: n.x(),
          y: n.y() + trimOffsetOf(node),
          w: Math.max(5, (box ? box.w : n.width()) * sx),
          h: Math.max(5, (box ? box.h : n.height()) * sy),
          // Admin mode has no rotation handles, and the extractor bakes Figma
          // rotation into post-rotation AABBs with rotation=0 — persisting one
          // here would be an unreconcilable delta on the next re-sync.
          ...(policy.admin ? {} : { rotation: n.rotation() }),
        });
      },
    };
  };

  /** Installed on EVERY node that has an element id, not only the interactive
   *  ones: the linked-overlay follow has to reach unbound synthetic overlays,
   *  which get no edit props at all. */
  const refProps = (node: KonvaNode) => {
    const id = node.elementId;
    if (!id) {
      // Shadow clones are refed separately (they have no element id) so a row
      // move can hand them to the Transformer with their element.
      const owner = shadowCloneOwner(node);
      if (!owner) return {};
      const cloneId = node.props.id as string;
      return {
        ref: (n: any) => {
          if (n) shadowRefs.current.set(cloneId, { owner, node: n });
          else shadowRefs.current.delete(cloneId);
        },
      };
    }
    return {
      ref: (n: any) => {
        if (n) nodeRefs.current.set(id, n);
        else nodeRefs.current.delete(id);
      },
    };
  };

  // Figma leadingTrim=CAP_HEIGHT compensation: Konva has no trimmed line
  // boxes, so the drawn text must shift up for its cap-top to land on the
  // element's y (= Figma's trimmed bbox top). Measured per node with the
  // loaded font — fontsReady remounts the Text nodes, re-running this.
  const trimOffsetOf = (node: KonvaNode): number =>
    node.konvaType === 'Text' && (node.props as Record<string, unknown>)._leadingTrim
      ? leadingTrimYOffset(node.props as Record<string, unknown>)
      : 0;

  // Geometry/style for the HTML <textarea> overlay (editor-only). Mirror Konva's
  // text NODE exactly: same trim-shifted top (y - trimOffset = the line-box top)
  // and (via textOverlayStyle) the full fontSize*lineHeight line-box height. The
  // textarea's text is centred in that line-box by line-height just like Konva's
  // glyph, so the two line up. (Sizing the box to _boxH instead clipped the text.)
  const overlay = editingNode
    ? textOverlayStyle(
        { ...editingNode.props, y: (editingNode.props.y as number) - trimOffsetOf(editingNode) },
        scale,
      )
    : null;
  const overlayMeta = editingNode?.inputKey ? textMeta?.(editingNode.inputKey) : null;

  return (
    <div style={{
      position: 'relative',
      width: scene.width * scale,
      height: scene.height * scale,
      // Belt-and-suspenders with the per-node preventDefault below: outside a
      // reposition the browser owns vertical scrolling over the canvas
      // (pan-y also suppresses accidental double-tap/pinch page zoom); during
      // a reposition all native gestures yield to the drag/pinch.
      // ...and NOT in admin: the admin board uses the same lock purely for its
      // selection semantics, but it has no pinch/pan family to protect (see the
      // `!admin` gate on the gesture effect), so taking touch-action away would
      // only cost the operator vertical scrolling over a tall board.
      // Row mode is a lock too (and is end-user only by construction).
      touchAction: editable && locked && !admin ? 'none' : 'pan-y',
    }}>
      <Stage
        width={scene.width * scale}
        height={scene.height * scale}
        scaleX={scale}
        scaleY={scale}
        // Row mode: snapshot the group BEFORE the drag moves anything. Pointer
        // events precede mouse/touch ones, and a drag only starts on a move.
        onPointerDown={groupMode ? snapshotGroup : undefined}
        onMouseDown={(e: any) => {
          // During a reposition lock, canvas clicks must NOT change selection
          // (finish only by clicking outside the board / Escape / Apply).
          if (!editable || locked) return;
          const target = e.target;
          const id = target === target.getStage() ? null : target.id?.();
          // Free mode: deselect when clicking the empty stage, the background, or
          // any NON-editable node. Editable nodes keep selection (onClick re-selects).
          if (!id || !editableIds.has(id)) onSelect?.(null);
        }}
      >
        <Layer>
          {nodes.map((node, i) => {
            const key = `${(node.props.id as string) ?? 'node'}:${i}`;
            const ep0 = { ...refProps(node), ...makeEditProps(node) };
            // An occluding full-board overlay (collage frame/foreground) is set
            // non-listening so clicks fall straight through to the cells beneath
            // it — Konva hit-tests an image by its whole box, and we can't do
            // per-pixel (alpha) hit because the cross-origin photos taint the
            // canvas (getImageData throws "operation is insecure").
            const isOverlay = policy.inert(node);
            // Reposition lock: only the target listens, so the drag stays on it
            // (through occluding layers) and other layers ignore pointer events.
            // preventDefault: Konva kills native page scrolling on ANY touch that
            // starts on a shape with preventDefault=true (its default) — so a
            // mobile scroll gesture that lands on the board freezes the page.
            // Only the reposition target actually needs to block scrolling (its
            // drag/pinch would fight the scroll); everything else lets the
            // browser scroll natively. Taps/clicks are unaffected.
            // Row mode: the targets are every member of the row.
            const target = locked && isLockTarget(node);
            const ep = locked
              ? { ...ep0, listening: target, preventDefault: target }
              : (isOverlay
                  ? { ...ep0, listening: false, preventDefault: false }
                  : { ...ep0, preventDefault: false });
            if (node.konvaType === 'Text') {
              // Hide the canvas text while its textarea overlay is open (avoid double text).
              // Key on fontsReady so the node remounts + re-measures once the real
              // font has loaded (Konva caches text metrics at construction) — the
              // leading-trim shift below is measured with the loaded font then too.
              const trimOffset = trimOffsetOf(node);
              // Shifts the node up AND moves a text gradient down by the same
              // offset so the band stays on the glyph cap (see applyLeadingTrimShift).
              const tp = applyLeadingTrimShift(node.props as Record<string, unknown>, trimOffset);
              return <Text key={`${key}:${fontsReady}`} {...tp} {...ep} visible={node.elementId !== editingId} />;
            }
            if (node.konvaType === 'Rect') return <Rect key={key} {...node.props} {...ep} />;
            // Row mode moves boxes; a collage cell in the row must not pan.
            const panMode = editable && !groupMode && node.elementId === selectedId && isPannableImage(node);
            return (
              <SceneImage
                key={key}
                node={node}
                editProps={ep}
                onLoad={handleImageLoad}
                scale={scale}
                panMode={panMode}
                onPan={node.elementId ? (pan) => onTransformChange?.(node.elementId!, { panX: pan.x, panY: pan.y, panZoom: pan.zoom }) : undefined}
              />
            );
          })}
          {/* Reposition indicator: while a collage cell is being repositioned,
              trace its mask outline (the tilted quad, not a bounding box) as a
              screen-constant accent stroke so it's clear WHICH cell the drag /
              zoom is affecting. listening:false so it never intercepts the pan. */}
          {editable && (() => {
            const sel = selectedId ? nodes.find((n) => n.elementId === selectedId) : null;
            if (!sel || !isPannableImage(sel)) return null;
            const p = sel.props as Record<string, any>;
            const mask = p._shapeMask as { path?: string } | undefined;
            if (!mask?.path) return null;
            return (
              <Shape
                key="reposition-outline"
                x={p.x as number}
                y={p.y as number}
                listening={false}
                stroke="#1A8CFF"
                strokeWidth={2.5}
                strokeScaleEnabled={false}
                shadowColor="rgba(0,0,0,0.55)"
                shadowBlur={3}
                sceneFunc={(ctx: any, shape: any) => {
                  ctx.beginPath();
                  traceMaskPath(ctx, mask.path as string, p.width as number, p.height as number);
                  ctx.closePath();
                  ctx.strokeShape(shape);
                }}
              />
            );
          })()}
          {/* Editable-text discoverability hint — mirrors the legacy editor's
              `[contenteditable="true"] { outline: 1px dashed black }` rule. Shown for
              every editable text in editor mode and hidden on the currently selected
              element (Transformer takes over) or the one being text-edited (textarea
              overlay has its own outline). `listening:false` keeps clicks falling
              through to the real text node; `strokeScaleEnabled:false` keeps the
              stroke at 1 device-pixel regardless of stage scale. */}
          {editable && !locked && showTextHints && nodes.map((node, i) => {
            if (!policy.textHint(node)) return null;
            if (node.elementId === selectedId || node.elementId === editingId) return null;
            const p = node.props as Record<string, any>;
            return (
              <Rect
                key={`hint:${node.elementId}:${i}`}
                x={p.x as number}
                y={p.y as number}
                width={p.width as number}
                height={(p._boxH as number) || 0}
                rotation={(p.rotation as number) || 0}
                listening={false}
                stroke="black"
                strokeWidth={1}
                strokeScaleEnabled={false}
                dash={[4, 4]}
              />
            );
          })}
          {editable && (() => {
            // The rotate handle hangs rotateAnchorOffset SCREEN px above the
            // element, but the Stage clips it — an element near the board's top
            // edge would put it off-canvas, invisible and untouchable. Pull it
            // in so it always stays on the board (it may sit close to the
            // top-center resize anchor for edge-hugging elements; reachable
            // beats pretty). Unrotated top approximation is fine: once rotated,
            // the handle swings toward the board's interior anyway.
            const selTop = (() => {
              if (!selectedId) return Infinity;
              const n = nodes.find((nd) => nd.elementId === selectedId);
              return n ? ((n.props.y as number) ?? 0) * scale : Infinity;
            })();
            const half = (COARSE_POINTER ? TOUCH_ANCHOR_PX : 10) / 2;
            const pad = COARSE_POINTER ? TOUCH_ANCHOR_HIT_PAD_PX : 0;
            const rotateOffset = Math.max(4, Math.min(ROTATE_ANCHOR_OFFSET_PX, selTop - half - pad));
            // Per-node: a user TEXT layer never rotates (a rotated text can no
            // longer be inline-edited, so the handle would be a one-way trap).
            const selNode = selectedId ? nodes.find((nd) => nd.elementId === selectedId) : null;
            return (
              <Transformer
                ref={trRef}
                // Row mode is drag-only: one frame around the row, no handles,
                // and its whole area grabbable so the gaps between members
                // move the row too.
                rotateEnabled={groupMode ? false : (selNode ? policy.rotatable(selNode) : policy.rotateEnabled)}
                resizeEnabled={!groupMode}
                shouldOverdrawWholeArea={groupMode}
                ignoreStroke
                anchorSize={COARSE_POINTER ? TOUCH_ANCHOR_PX : 10}
                rotateAnchorOffset={rotateOffset}
                anchorStyleFunc={COARSE_POINTER ? (a: any) => padAnchorHitArea(a, TOUCH_ANCHOR_HIT_PAD_PX) : undefined}
                boundBoxFunc={(oldBox: any, newBox: any) => {
                  const min = adminMinBox();
                  return newBox.width < min.width || newBox.height < min.height ? oldBox : newBox;
                }}
              />
            );
          })()}
          {/* Board centre guides. Always mounted, toggled imperatively during a
              drag (see setGuides) so a snap costs no React re-render — one
              mid-gesture re-render would reset the node Konva is dragging.
              strokeWidth is divided by the stage scale so the line stays one
              screen pixel at any zoom. */}
          {admin && (
            <>
              <Line
                ref={guideXRef}
                points={[scene.width / 2, 0, scene.width / 2, scene.height]}
                stroke="#22d3ee"
                strokeWidth={1 / (scale || 1)}
                listening={false}
                visible={false}
              />
              <Line
                ref={guideYRef}
                points={[0, scene.height / 2, scene.width, scene.height / 2]}
                stroke="#22d3ee"
                strokeWidth={1 / (scale || 1)}
                listening={false}
                visible={false}
              />
            </>
          )}
        </Layer>
      </Stage>
      {editingNode && overlay && (
        <textarea
          key={editingId ?? 'edit'}
          autoFocus
          defaultValue={String(editingNode.props.text ?? '')}
          maxLength={overlayMeta?.maxChars}
          // `auto` = first-strong-character base direction, matching the Konva
          // text's paragraphDirection — so Hebrew-first edits go RTL and English/
          // digits-first edits stay LTR (forcing rtl mangled Latin-first input).
          dir="auto"
          spellCheck={false}
          style={{
            position: 'absolute',
            left: overlay.left,
            top: overlay.top,
            width: overlay.width,
            height: overlay.height || undefined, // Konva line-box height (max with _boxH for multi-line)
            fontSize: overlay.fontSize,
            lineHeight: String(overlay.lineHeight),
            letterSpacing: overlay.letterSpacing,
            textAlign: overlay.textAlign as React.CSSProperties['textAlign'],
            fontFamily: overlay.fontFamily,
            color: overlay.color,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'break-word',
            margin: 0,
            padding: 0,
            border: 'none',
            outline: '1px solid #1976d2', // outline doesn't shift text (border would)
            background: 'rgba(255,255,255,0.06)',
            resize: 'none',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}
          onChange={(e) => {
            const v = sanitizeTextInput(e.target.value, { maxChars: overlayMeta?.maxChars, multiline: overlayMeta?.multiline });
            if (v !== e.target.value) {
              // Clamp the caret: sanitizing may remove chars before it.
              const pos = Math.min(e.target.selectionStart ?? v.length, v.length);
              e.target.value = v;
              e.target.setSelectionRange(pos, pos);
            }
            if (editingNode.inputKey) {
              editingValueRef.current = { inputKey: editingNode.inputKey, value: v };
              onTextChange?.(editingNode.inputKey, v); // live, like legacy oninput
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              closeEdit(); // value already applied live (legacy parity)
            } else if (e.key === 'Enter' && !overlayMeta?.multiline) {
              e.preventDefault();
              closeEdit();
            }
          }}
          onBlur={closeEdit}
        />
      )}
    </div>
  );
}

export default SceneRenderer;
